import { InputBase, Collapse, IconButton } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateGeoChartStyleOptions } from '../../../redux/ChartPoperties/ChartControlsActions';
import { ChartConGeoChartControls } from '../../../redux/ChartPoperties/ChartControlsInterface';
import { fieldName } from '../../CommonFunctions/CommonFunctions';
import ChartColors from './ChartColors';
import SwitchWithInput from '../SwitchWithInput';
import { RootState } from '../../../redux';
import { OrientationRadioGroup } from '../Legend/LegendComponents/OrientationRadioGroup';
import ColorPickerPopover from './ChartColorComponents/ColorPickerPopover';

const inputBaseStyle = {
  border: '2px solid rgba(224,224,224,1)',
  borderRadius: '3px',
  height: '20px',
  fontSize: '12px',
  padding: '0px 4px',
  color: '#a7a7a7',
};

const BubbleMapColors = ({ iconColor }: { iconColor?: string }) => {
  const { chartControls, tabTileProps, chartProperties } = useSelector((state: RootState) => ({
    chartControls: state.chartControls,
    tabTileProps: state.tabTileProps,
    chartProperties: state.chartProperties,
  }));

  const propKey: string = `${tabTileProps.selectedTabId}.${tabTileProps.selectedTileId}`;

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isExpanded, setIsExpanded] = useState<boolean>(true);

  const geoStyle: ChartConGeoChartControls =
    chartControls.properties[propKey].geoChartControls || {};

  const [selectedColor, setSelectedColor] = useState<string>('');
  const [activeOptionKey, setActiveOptionKey] = useState<string>('');
  const [activeSliceKey, setActiveSliceKey] = useState<string | null>(null);

  let _dimensionField = chartProperties.properties[propKey].chartAxes[1];
  let dimName = fieldName(_dimensionField.fields[0]);

  const dispatch = useDispatch();

  const hasDimension = !!chartProperties.properties[propKey].chartAxes[1].fields[0];
  const minVal = Number(geoStyle.minValue);
  const maxVal = Number(geoStyle.maxValue);
  const minMaxError =
    geoStyle.minValue !== '' &&
    geoStyle.maxValue !== '' &&
    !isNaN(minVal) &&
    !isNaN(maxVal) &&
    minVal >= maxVal;

  const handleColorChange = (newColor: string) => {
    setSelectedColor(newColor);

    if (activeSliceKey !== null) {
      const newBgColors = {
        ...geoStyle.bgCol,
        [activeSliceKey]: newColor,
      };
      dispatch(updateGeoChartStyleOptions(propKey, 'bgCol', newBgColors));
    } else if (activeOptionKey) {
      dispatch(updateGeoChartStyleOptions(propKey, activeOptionKey, newColor));
    }
  };

  const renderSmallColorBox = (
    currentColor: string,
    optionKey: string,
    isSlice: boolean = false,
  ) => (
    <div
      style={{
        height: '1rem',
        width: '30px',
        minWidth: '30px',
        backgroundColor: currentColor,
        border: '1px solid darkgray',
        cursor: 'pointer',
        borderRadius: '2px',
        marginRight: '10px',
      }}
      onClick={(e) => {
        e.stopPropagation();
        setSelectedColor(currentColor);

        if (isSlice) {
          setActiveSliceKey(optionKey);
          setActiveOptionKey('');
        } else {
          setActiveOptionKey(optionKey);
          setActiveSliceKey(null);
        }

        setAnchorEl(e.currentTarget);
      }}
    />
  );

  return (
    <div style={{ marginTop: '20px', paddingTop: '8px', paddingLeft: '4px', overflowX: 'hidden' }}>
      <OrientationRadioGroup
        orientation={geoStyle.manualColor ? 'manual' : 'auto'}
        options={[
          { name: 'Auto', key: 'auto' },
          { name: 'Manual', key: 'manual' },
        ]}
        onChange={(key) => {
          dispatch(updateGeoChartStyleOptions(propKey, 'manualColor', key === 'manual'));
        }}
        style={{ width: '94%', marginBottom: '10px' }}
      />

      {/* ---------------- AUTO MODE ---------------- */}
      {!geoStyle.manualColor ? (
        <div>
          <div style={{ marginTop: '-25px' }}>
            <ChartColors iconColor={iconColor} />
          </div>

          {!hasDimension && (
            <>
              <div
                className="optionDescription"
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  paddingLeft: '1px',
                  marginTop: '10px',
                }}
              >
                <label
                  htmlFor="divergentMode"
                  className="enableDisableLabel"
                  style={{
                    marginRight: '10px',
                    fontWeight: 600,
                    color: iconColor ? iconColor : '#404040',
                  }}
                >
                  Use Divergent Colors
                </label>
                <div
                  style={{
                    width: '50px',
                    height: '24px',
                    display: 'flex',
                    transform: 'translateX(60px)',
                    marginBottom: '7px',
                  }}
                >
                  <SwitchWithInput
                    isChecked={!!geoStyle.isDivergentModeEnabled}
                    onSwitch={() =>
                      dispatch(
                        updateGeoChartStyleOptions(
                          propKey,
                          'isDivergentModeEnabled',
                          !geoStyle.isDivergentModeEnabled,
                        ),
                      )
                    }
                  />
                </div>
              </div>
            </>
          )}
        </div>
      ) : (
        /* ---------------- MANUAL MODE ---------------- */
        <div>
          {!hasDimension ? (
            <>
              <div
                className="optionDescription"
                style={{
                  fontSize: '12px',
                  marginTop: '-5px',
                  color: iconColor ? iconColor : '#404040',
                }}
              >
                Min
              </div>
              <div
                className="optionDescription"
                style={{
                  marginTop: '0px',
                  paddingTop: '0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                }}
              >
                {renderSmallColorBox(geoStyle.minColor, 'minColor')}
                <InputBase
                  style={{ height: '1.25rem', width: '50%' }}
                  value={geoStyle.minValue}
                  onChange={(e: any) => {
                    dispatch(updateGeoChartStyleOptions(propKey, 'minValue', e.target.value));
                  }}
                  sx={inputBaseStyle}
                  placeholder={'Enter Min Value'}
                />
              </div>

              <div
                className="optionDescription"
                style={{
                  fontSize: '12px',
                  marginTop: '5px',
                  color: iconColor ? iconColor : '#404040',
                }}
              >
                Max
              </div>
              <div
                className="optionDescription"
                style={{
                  marginTop: '0px',
                  paddingTop: '0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                }}
              >
                {renderSmallColorBox(geoStyle.maxColor, 'maxColor')}
                <InputBase
                  style={{ height: '1.25rem', width: '50%' }}
                  value={geoStyle.maxValue}
                  onChange={(e: any) => {
                    dispatch(updateGeoChartStyleOptions(propKey, 'maxValue', e.target.value));
                  }}
                  sx={inputBaseStyle}
                  placeholder={'Enter Max Value'}
                />
              </div>

              {minMaxError && (
                <div
                  style={{
                    color: '#d32f2f',
                    fontSize: '12px',
                    marginTop: '5px',
                    marginBottom: '5px',
                    textAlign: 'left',
                    paddingLeft: '10px',
                  }}
                >
                  Max value should be greater than Min Value
                </div>
              )}

              <div
                className="optionDescription"
                style={{
                  marginTop: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                }}
              >
                {renderSmallColorBox(geoStyle.borderColor, 'borderColor')}
                <div style={{ fontSize: '12px', color: iconColor ? iconColor : '#404040' }}>
                  Border Color
                </div>
              </div>

              <div
                className="optionDescription"
                style={{
                  marginTop: '10px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                }}
              >
                {renderSmallColorBox(geoStyle.areaColor, 'areaColor')}
                <div style={{ fontSize: '12px', color: iconColor ? iconColor : '#404040' }}>
                  Area Color
                </div>
              </div>

              <div
                className="optionDescription"
                style={{
                  fontWeight: 600,
                  marginTop: '15px',
                  color: iconColor ? iconColor : '#404040',
                }}
              >
                EMPHASIS ON MOUSEOVER:
              </div>
              <div
                className="optionDescription"
                style={{
                  marginTop: '5px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  marginBottom: '15px',
                }}
              >
                {renderSmallColorBox(geoStyle.emphasisAreaColor, 'emphasisAreaColor')}
                <div style={{ fontSize: '12px', color: iconColor ? iconColor : '#404040' }}>
                  Area Color
                </div>
              </div>
            </>
          ) : (
            <>
              <div
                className="optionDescription"
                style={{
                  marginTop: '-5px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                }}
              >
                {renderSmallColorBox(geoStyle.borderColor, 'borderColor')}
                <div style={{ fontSize: '12px', color: iconColor ? iconColor : '#404040' }}>
                  Border Color
                </div>
              </div>

              <div
                className="optionDescription"
                style={{
                  marginTop: '-5px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                }}
              >
                {renderSmallColorBox(geoStyle.areaColor, 'areaColor')}
                <div style={{ fontSize: '12px', color: iconColor ? iconColor : '#404040' }}>
                  Area Color
                </div>
              </div>

              <div
                className="optionDescription"
                style={{
                  fontWeight: 600,
                  marginTop: '15px',
                  color: iconColor ? iconColor : '#404040',
                }}
              >
                EMPHASIS ON MOUSEOVER:
              </div>

              <div
                className="optionDescription"
                style={{
                  marginTop: '-5px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                }}
              >
                {renderSmallColorBox(geoStyle.emphasisAreaColor, 'emphasisAreaColor')}
                <div style={{ fontSize: '12px', color: iconColor ? iconColor : '#404040' }}>
                  Area Color
                </div>
              </div>

              {/* Collapsible Header */}
              <div
                className="optionDescription"
                style={{
                  fontWeight: 600,
                  marginBottom: '5px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  paddingRight: '10px',
                }}
              >
                <span style={{ color: iconColor ? iconColor : '#404040' }}>{dimName} Colors:</span>
                <IconButton
                  size="small"
                  onClick={() => setIsExpanded(!isExpanded)}
                  style={{ padding: 2 }}
                  title={isExpanded ? 'Collapse List' : 'Expand List'}
                >
                  {isExpanded ? (
                    <KeyboardArrowUpIcon
                      fontSize="small"
                      sx={{ color: iconColor ? iconColor : '#404040' }}
                    />
                  ) : (
                    <KeyboardArrowDownIcon
                      fontSize="small"
                      sx={{ color: iconColor ? iconColor : '#404040' }}
                    />
                  )}
                </IconButton>
              </div>

              {/* Collapsible List */}
              <Collapse in={isExpanded}>
                <div
                  className="optionDescription"
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    marginTop: '-15px',
                    justifyContent: 'flex-start',
                    alignItems: 'flex-start',
                  }}
                >
                  {Object.keys(geoStyle.bgCol).map((item: any) => {
                    if (item !== '') {
                      const displayColor = geoStyle.bgCol[item];
                      return (
                        <div
                          key={item}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            marginTop: 8,
                            width: '100%',
                          }}
                        >
                          {renderSmallColorBox(displayColor, item, true)}

                          <label
                            style={{
                              fontSize: '12px',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              cursor: 'default',
                              flexGrow: 1,
                              textAlign: 'left',
                              color: iconColor ? iconColor : '#404040',
                            }}
                            title={`${dimName} : ${item}`}
                          >
                            {item}
                          </label>
                        </div>
                      );
                    }
                    return null;
                  })}
                </div>
              </Collapse>
            </>
          )}

          <ColorPickerPopover
            isOpen={Boolean(anchorEl)}
            anchorEl={anchorEl}
            onClose={() => setAnchorEl(null)}
            currentColor={selectedColor}
            onColorChange={handleColorChange}
            propKey={propKey}
            selectedMenu=""
          />
        </div>
      )}
    </div>
  );
};

export default BubbleMapColors;
